import { useCallback, useEffect, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { InsightsCapabilities } from '@pushly/insights-sdk/lib/insights-capabilities'
import { InsightsService } from '../../../services/insights'
import { GoodDirection } from '../types'

/** The part of a served insights document that the dashboard reads: each measure's field variant and its declared direction. */
type MetadataDocument = {
    properties?: { fields?: { items?: { oneOf?: Array<{ const?: unknown; 'x-goodDirection'?: unknown }> } } }
}

const GOOD_DIRECTIONS: readonly string[] = ['higher', 'lower', 'none']

const isGoodDirection = (value: unknown): value is GoodDirection =>
    typeof value === 'string' && GOOD_DIRECTIONS.includes(value)

/**
 * Whether a bigger value of a measure is good, as the metadata document declares it on the measure's
 * field variant. Undefined when the document says nothing, which callers read as "do not colour".
 * Nothing about a measure's direction is decided anywhere else.
 */
export const goodDirectionFor = (
    document: MetadataDocument | undefined,
    measureName: string,
): GoodDirection | undefined => {
    const fieldVariants = document?.properties?.fields?.items?.oneOf ?? []
    const declared = fieldVariants.find((variant) => variant?.const === measureName)?.['x-goodDirection']
    return isGoodDirection(declared) ? declared : undefined
}

type RegistryDocuments = { notifications: MetadataDocument; prompts: MetadataDocument }
type LoadedRegistry = RegistryDocuments & {
    notificationCapabilities: InsightsCapabilities
    promptCapabilities: InsightsCapabilities
    loadedAt: Date
}

/** The last registry read for each organization this session, kept to fall back on when a later read fails. */
const lastLoadedRegistryByOrganization = new Map<number, LoadedRegistry>()

export const clearOrgDashboardRegistryCache = (): void => lastLoadedRegistryByOrganization.clear()

const readRegistry = async (organizationId: number): Promise<LoadedRegistry> => {
    const insights = Container.get(InsightsService)
    const scope = { accountId: organizationId }
    const options = { grain: 'domain' }
    const [notifications, prompts] = await Promise.all([
        insights.fetchMetadata('notifications', scope, undefined, options),
        insights.fetchMetadata('prompts', scope, undefined, options),
    ])
    return {
        notifications,
        prompts,
        notificationCapabilities: new InsightsCapabilities(notifications),
        promptCapabilities: new InsightsCapabilities(prompts),
        loadedAt: new Date(),
    }
}

type RegistryState = { status: 'loading' | 'ready' | 'cached' | 'unavailable'; registry?: LoadedRegistry }

/**
 * Both entities' insights documents at organization scope, read by domain. The documents are kept
 * alongside their parsed form because the parsed form does not carry which direction of change is
 * good for a measure; see `goodDirectionFor`.
 */
export const useOrgDashboardRegistry = (organizationId: number) => {
    const [state, setState] = useState<RegistryState>({ status: 'loading' })
    const [attempt, setAttempt] = useState(0)

    useEffect(() => {
        let cancelled = false
        setState({ status: 'loading' })
        readRegistry(organizationId)
            .then((registry) => {
                if (cancelled) return
                lastLoadedRegistryByOrganization.set(organizationId, registry)
                setState({ status: 'ready', registry })
            })
            .catch(() => {
                if (cancelled) return
                const lastLoaded = lastLoadedRegistryByOrganization.get(organizationId)
                setState(lastLoaded ? { status: 'cached', registry: lastLoaded } : { status: 'unavailable' })
            })
        return () => {
            cancelled = true
        }
    }, [organizationId, attempt])

    const loadedRegistry = state.registry
    const goodDirectionForMeasure = useCallback(
        (measureName: string): GoodDirection | undefined =>
            goodDirectionFor(loadedRegistry?.notifications, measureName) ??
            goodDirectionFor(loadedRegistry?.prompts, measureName),
        [loadedRegistry],
    )
    const retry = useCallback(() => setAttempt((count) => count + 1), [])

    return {
        status: state.status,
        notifications: loadedRegistry?.notificationCapabilities,
        prompts: loadedRegistry?.promptCapabilities,
        cachedAt: state.status === 'cached' ? loadedRegistry?.loadedAt : undefined,
        goodDirectionFor: goodDirectionForMeasure,
        retry,
    }
}
